import * as THREE from 'three';
import RAPIER from '@dimforge/rapier3d-compat';

export const WORLD_LIMIT = 300;
export const FREE_DRIVE_SPAWN = { x: 0, y: 1.35, z: 82 } as const;
export const FREE_DRIVE_YAW = Math.PI;

interface RoadSpec {
  x: number;
  z: number;
  width: number;
  length: number;
  yaw?: number;
}

/**
 * Grosse, flache Fahr- und Parkwelt. Die frueheren Balkenrampen und hohen
 * Testhindernisse sind bewusst entfernt: Ein Kind soll die Strecke lesen und
 * nicht an einem Physik-Pruefstand umkippen.
 */
export class TestArea {
  readonly group = new THREE.Group();
  private readonly roads: readonly RoadSpec[] = [
    { x: 0, z: 0, width: 24, length: 540 },
    { x: 118, z: 20, width: 22, length: 300 },
    { x: -118, z: -40, width: 22, length: 300 },
    { x: 0, z: -132, width: 22, length: 430, yaw: Math.PI / 2 },
    { x: 0, z: 126, width: 22, length: 430, yaw: Math.PI / 2 },
    { x: 59, z: 18, width: 20, length: 118, yaw: Math.PI / 2 },
    { x: -59, z: -38, width: 20, length: 118, yaw: Math.PI / 2 },
  ];

  constructor(scene: THREE.Scene, world: RAPIER.World) {
    const grass = new THREE.MeshStandardMaterial({ map: makeGrassTexture(), roughness: 1 });
    const ground = new THREE.Mesh(new THREE.PlaneGeometry(700, 700), grass);
    ground.rotation.x = -Math.PI / 2;
    ground.receiveShadow = true;
    this.group.add(ground);

    const groundBody = world.createRigidBody(RAPIER.RigidBodyDesc.fixed().setTranslation(0, -0.5, 0));
    world.createCollider(RAPIER.ColliderDesc.cuboid(350, 0.5, 350).setFriction(1.05), groundBody);

    const asphalt = new THREE.MeshStandardMaterial({ color: 0x3f454c, roughness: 0.94, metalness: 0 });
    for (const road of this.roads) this.addRoad(road, asphalt);
    this.addRoadMarkings();
    this.addParkingLot();
    this.addTown(world);
    this.addRoadsideDetails();

    scene.add(this.group);
  }

  isOutsideSafeWorld(position: { x: number; y: number; z: number }): boolean {
    return position.y < -2 || Math.abs(position.x) > WORLD_LIMIT || Math.abs(position.z) > WORLD_LIMIT;
  }

  /** Keine dynamischen Weltobjekte mehr; API bleibt fuer Game kompatibel. */
  syncDynamics(): void {}

  private addRoad(spec: RoadSpec, material: THREE.Material): void {
    const sidewalk = new THREE.Mesh(
      new THREE.PlaneGeometry(spec.width + 5, spec.length),
      new THREE.MeshStandardMaterial({ color: 0xa7a9a6, roughness: 0.96 }),
    );
    sidewalk.rotation.set(-Math.PI / 2, 0, spec.yaw ?? 0);
    sidewalk.position.set(spec.x, 0.014, spec.z);
    sidewalk.receiveShadow = true;
    this.group.add(sidewalk);

    const road = new THREE.Mesh(new THREE.PlaneGeometry(spec.width, spec.length), material);
    road.rotation.set(-Math.PI / 2, 0, spec.yaw ?? 0);
    road.position.set(spec.x, 0.025, spec.z);
    road.receiveShadow = true;
    this.group.add(road);

    const edgeMaterial = new THREE.MeshBasicMaterial({ color: 0xe7e4dc });
    for (const side of [-1, 1]) {
      const edge = new THREE.Mesh(new THREE.PlaneGeometry(0.24, spec.length * 0.96), edgeMaterial);
      edge.rotation.set(-Math.PI / 2, 0, spec.yaw ?? 0);
      const offset = new THREE.Vector3((spec.width / 2 - 0.65) * side, 0, 0)
        .applyAxisAngle(UP, -(spec.yaw ?? 0));
      edge.position.set(spec.x + offset.x, 0.04, spec.z + offset.z);
      this.group.add(edge);
    }
  }

  private addRoadMarkings(): void {
    const positions: { x: number; z: number; yaw: number }[] = [];
    for (const road of this.roads) {
      for (let d = -road.length / 2 + 8; d < road.length / 2 - 5; d += 10) {
        const yaw = road.yaw ?? 0;
        positions.push({
          x: road.x + Math.sin(yaw) * d,
          z: road.z + Math.cos(yaw) * d,
          yaw,
        });
      }
    }
    const geometry = new THREE.BoxGeometry(0.22, 0.025, 4.2);
    const material = new THREE.MeshBasicMaterial({ color: 0xf6e8a4 });
    const stripes = new THREE.InstancedMesh(geometry, material, positions.length);
    const matrix = new THREE.Matrix4();
    const quaternion = new THREE.Quaternion();
    for (let i = 0; i < positions.length; i++) {
      const p = positions[i];
      if (!p) continue;
      quaternion.setFromAxisAngle(UP, p.yaw);
      matrix.compose(new THREE.Vector3(p.x, 0.052, p.z), quaternion, ONE);
      stripes.setMatrixAt(i, matrix);
    }
    this.group.add(stripes);
  }

  private addParkingLot(): void {
    const lot = new THREE.Mesh(
      new THREE.PlaneGeometry(100, 82),
      new THREE.MeshStandardMaterial({ color: 0x4a5056, roughness: 0.96 }),
    );
    lot.rotation.x = -Math.PI / 2;
    lot.position.set(70, 0.035, 67);
    lot.receiveShadow = true;
    this.group.add(lot);

    const lineMaterial = new THREE.MeshBasicMaterial({ color: 0xf3f0dd });
    const lineGeo = new THREE.PlaneGeometry(0.18, 9);
    const lines = new THREE.InstancedMesh(lineGeo, lineMaterial, 36);
    const matrix = new THREE.Matrix4();
    let index = 0;
    for (const rowZ of [40, 94]) {
      for (let x = 38.5; x <= 98; x += 3.5) {
        if (index >= 36) break;
        matrix.makeRotationX(-Math.PI / 2);
        matrix.setPosition(x, 0.055, rowZ);
        lines.setMatrixAt(index++, matrix);
      }
    }
    lines.count = index;
    this.group.add(lines);
  }

  private addTown(world: RAPIER.World): void {
    const buildings = [
      [-55, -70, 24, 15, 32], [-90, -70, 20, 12, 26], [64, -72, 30, 18, 28],
      [105, -72, 22, 14, 30], [-60, 65, 28, 13, 30], [-102, 70, 22, 18, 22],
      [164, 65, 30, 16, 28], [164, 105, 24, 13, 24], [-168, -95, 28, 16, 34],
      [-165, -30, 26, 12, 30], [170, -125, 26, 19, 34], [150, -175, 32, 15, 28],
      [-60, 178, 28, 14, 34], [15, 185, 32, 18, 32], [95, 180, 26, 13, 28],
    ] as const;
    const wallMaterials = [0xd7c5ae, 0xc6d4da, 0xe2c8b6].map(
      (color) => new THREE.MeshStandardMaterial({ color, roughness: 0.88 }),
    );
    const roofMaterial = new THREE.MeshStandardMaterial({ color: 0x454a50, roughness: 0.82 });
    const windowMatrices: THREE.Matrix4[] = [];
    const windowPosition = new THREE.Vector3();
    const windowRotation = new THREE.Quaternion();
    const windowScale = new THREE.Vector3(1.45, 1.25, 1);
    for (let i = 0; i < buildings.length; i++) {
      const item = buildings[i];
      if (!item) continue;
      const [x, z, width, height, depth] = item;
      const building = new THREE.Mesh(new THREE.BoxGeometry(width, height, depth), wallMaterials[i % wallMaterials.length]);
      building.position.set(x, height / 2, z);
      building.castShadow = true;
      building.receiveShadow = true;
      this.group.add(building);
      const roof = new THREE.Mesh(new THREE.BoxGeometry(width + 1, 0.55, depth + 1), roofMaterial);
      roof.position.set(x, height + 0.28, z);
      roof.castShadow = true;
      this.group.add(roof);
      const floors = Math.max(2, Math.floor((height - 1) / 3));
      const columns = Math.max(2, Math.min(6, Math.floor(width / 4)));
      for (const side of [-1, 1]) {
        windowRotation.setFromAxisAngle(UP, side < 0 ? Math.PI : 0);
        for (let floor = 0; floor < floors; floor++) {
          for (let column = 0; column < columns; column++) {
            const wx = x - width * 0.38 + (column / Math.max(1, columns - 1)) * width * 0.76;
            const wy = 1.7 + floor * 2.8;
            if (wy > height - 0.7) continue;
            windowPosition.set(wx, wy, z + side * (depth / 2 + 0.018));
            windowMatrices.push(new THREE.Matrix4().compose(windowPosition, windowRotation, windowScale));
          }
        }
      }
      const body = world.createRigidBody(RAPIER.RigidBodyDesc.fixed().setTranslation(x, height / 2, z));
      world.createCollider(RAPIER.ColliderDesc.cuboid(width / 2, height / 2, depth / 2).setFriction(0.8), body);
    }
    const windows = new THREE.InstancedMesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.MeshStandardMaterial({ color: 0x18354c, emissive: 0x163148, emissiveIntensity: 0.3, roughness: 0.28, metalness: 0.18 }),
      windowMatrices.length,
    );
    for (let i = 0; i < windowMatrices.length; i++) {
      const matrix = windowMatrices[i];
      if (matrix) windows.setMatrixAt(i, matrix);
    }
    this.group.add(windows);
  }

  private addRoadsideDetails(): void {
    const treeCount = 52;
    const trunks = new THREE.InstancedMesh(
      new THREE.CylinderGeometry(0.28, 0.42, 2.8, 7),
      new THREE.MeshStandardMaterial({ color: 0x69442a, roughness: 1 }),
      treeCount,
    );
    const crowns = new THREE.InstancedMesh(
      new THREE.ConeGeometry(1.8, 4.8, 9),
      new THREE.MeshStandardMaterial({ color: 0x2b7541, roughness: 1 }),
      treeCount,
    );
    trunks.castShadow = crowns.castShadow = true;
    const matrix = new THREE.Matrix4();
    for (let i = 0; i < treeCount; i++) {
      const angle = (i / treeCount) * Math.PI * 2;
      const radius = 225 + (i % 5) * 10;
      const x = Math.cos(angle) * radius;
      const z = Math.sin(angle) * radius;
      matrix.makeTranslation(x, 1.4, z);
      trunks.setMatrixAt(i, matrix);
      matrix.makeTranslation(x, 4.7, z);
      crowns.setMatrixAt(i, matrix);
    }
    this.group.add(trunks, crowns);

    const lampPositions: { x: number; z: number }[] = [];
    for (let z = -245; z <= 245; z += 34) {
      lampPositions.push({ x: -15, z }, { x: 15, z });
    }
    const lamps = new THREE.InstancedMesh(
      new THREE.CylinderGeometry(0.12, 0.18, 5.6, 7),
      new THREE.MeshStandardMaterial({ color: 0x26323a, metalness: 0.55, roughness: 0.48 }),
      lampPositions.length,
    );
    for (let i = 0; i < lampPositions.length; i++) {
      const p = lampPositions[i];
      if (!p) continue;
      matrix.makeTranslation(p.x, 2.8, p.z);
      lamps.setMatrixAt(i, matrix);
    }
    this.group.add(lamps);
  }
}

function makeGrassTexture(): THREE.Texture {
  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D-Kontext nicht verfügbar');
  ctx.fillStyle = '#538f4d';
  ctx.fillRect(0, 0, size, size);
  const image = ctx.getImageData(0, 0, size, size);
  for (let i = 0; i < image.data.length; i += 4) {
    const noise = (Math.random() - 0.5) * 18;
    image.data[i] = clampByte((image.data[i] ?? 0) + noise);
    image.data[i + 1] = clampByte((image.data[i + 1] ?? 0) + noise);
    image.data[i + 2] = clampByte((image.data[i + 2] ?? 0) + noise * 0.7);
  }
  ctx.putImageData(image, 0, 0);
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(120, 120);
  texture.anisotropy = 4;
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

const clampByte = (value: number): number => value < 0 ? 0 : value > 255 ? 255 : value;
const UP = new THREE.Vector3(0, 1, 0);
const ONE = new THREE.Vector3(1, 1, 1);
